import {
  Alert,
  BottomSheet,
  Button,
  Card,
  RadialProgress,
  Picker,
  StatusPill,
  cn,
} from "@breenova/ui";
import { ArrowLeft, Egg as EggIcon, Plus } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router";

import { DetailHeader } from "@/components/app-shell";
import { WIDE_RECORD, useMediaQuery } from "@/lib/media";
import {
  incubationProgress,
  useAddEgg,
  useFosterEgg,
  useNest,
  useNests,
  useRingChick,
  useSetChickStatus,
  useSetEggStatus,
  type ChickDto,
  type EggDto,
  type EggStatus,
} from "@/lib/breeding";

/** The egg fates a breeder records from the nest screen, in the order they happen. */
const EGG_STATUSES: readonly { value: EggStatus; label: string }[] = [
  { value: "Laid", label: "Laid" },
  { value: "Fertile", label: "Fertile" },
  { value: "Infertile", label: "Clear" },
  { value: "DeadInShell", label: "Dead in shell" },
  { value: "Hatched", label: "Hatched" },
  { value: "Broken", label: "Broken" },
  { value: "Removed", label: "Removed" },
];

/**
 * How an egg's fate colours its tile.
 *
 * Three groups, not seven: a breeder glancing at a clutch is asking "how many are
 * coming", and the answer is good, bad, or not yet known. The precise fate is on the
 * tile in words for whoever needs it.
 */
function eggTileTone(status: EggStatus, fostered: boolean): { tile: string; ink: string } {
  if (fostered) {
    // A fostered egg keeps its own outline: whose egg it is matters more than its fate,
    // because the laying pair — not the rearing one — is the chick's parentage.
    return {
      tile: "border-2 border-dashed border-secondary bg-surface-container-lowest",
      ink: "text-secondary",
    };
  }

  if (status === "Fertile" || status === "Hatched") {
    return { tile: "bg-primary-fixed", ink: "text-on-primary-fixed-variant" };
  }

  if (status === "Infertile" || status === "DeadInShell" || status === "Broken") {
    return { tile: "bg-secondary-fixed", ink: "text-on-secondary-fixed-variant" };
  }

  // Laid and not yet candled: an outline, because nothing is known about it yet.
  return {
    tile: "border-2 border-dashed border-surface-container-highest",
    ink: "text-on-surface-variant",
  };
}

export function NestDetailPage({
  id: givenId,
  embedded = false,
}: {
  /** The nest to show, when the page is a pane beside a list rather than a route. */
  id?: string | undefined;
  /** Drawn in the split view's pane: no back button, and the wide layout at any width. */
  embedded?: boolean;
} = {}) {
  const params = useParams<{ id: string }>();
  const id = givenId ?? params.id;
  const nest = useNest(id);
  const addEgg = useAddEgg(id ?? "");
  const wide = useMediaQuery(WIDE_RECORD) || embedded;
  const [selectedEgg, setSelectedEgg] = useState<EggDto | null>(null);
  const [selectedChick, setSelectedChick] = useState<ChickDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (nest.isPending) {
    return <p className="p-lg text-body text-on-surface-variant">Loading nest…</p>;
  }

  if (nest.isError || !nest.data) {
    return (
      <div className="p-lg">
        <Alert tone="error">That nest could not be found.</Alert>
      </div>
    );
  }

  const { summary, eggs, chicks } = nest.data;
  const today = new Date().toISOString().slice(0, 10);
  const progress = incubationProgress(summary.firstEggOn, summary.expectedHatchOn, today);
  const where = `${summary.locationCode ? `Cage ${summary.locationCode} · ` : ""}round ${summary.round}`;

  async function handleAddEgg(): Promise<void> {
    setError(null);

    try {
      await addEgg.mutateAsync({ laidOn: null });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not add the egg.");
    }
  }

  const addEggButton = (
    <button
      type="button"
      onClick={() => void handleAddEgg()}
      disabled={addEgg.isPending}
      className={cn(
        "flex items-center gap-1.5 rounded-control",
        wide ? "h-touch px-lg" : "h-pill px-3.75",
        "bg-on-surface text-pill text-surface transition-opacity hover:opacity-90",
        "disabled:opacity-45",
      )}
    >
      <Plus className="size-4" aria-hidden />
      Add egg
    </button>
  );

  /*
   * The dial sits on the accent fill, so it inverts: the ring is drawn in ink on a
   * translucent ink track. The accent mark it uses elsewhere would disappear into the
   * sage behind it.
   */
  const hero = (
    <div
      className={cn(
        "flex flex-col items-center gap-3 rounded-sheet bg-primary-fixed-dim text-on-primary-fixed",
        wide ? "w-75 shrink-0 p-6" : "px-lg py-5.5",
      )}
    >
      {progress ? (
        <>
          <RadialProgress
            value={progress.progress}
            label="Incubation progress"
            tone="onAccent"
            strokeWidth={7.75}
            className="size-42"
          >
            <span className="text-label-caps uppercase">Day</span>
            <span className="text-day-hero" data-numeric>
              {Math.max(0, progress.day)}
            </span>
            <span className="text-label-caps uppercase" data-numeric>
              of {progress.total}
            </span>
          </RadialProgress>

          <span className={wide ? "text-pair-title" : "text-card-title"} data-numeric>
            Hatch due{" "}
            {wide ? shortWeekday(summary.expectedHatchOn) : longDay(summary.expectedHatchOn)}
          </span>
          <span className="flex h-7.5 items-center rounded-full bg-on-primary-fixed px-3.5 text-subtitle font-bold text-on-primary-container">
            {remaining(progress)}
          </span>
        </>
      ) : (
        <p className="text-body">No eggs yet — the hatch date follows the first one.</p>
      )}

      {summary.expectedRingingOn && !wide ? (
        <p className="text-caption" data-numeric>
          Ringing around {formatDay(summary.expectedRingingOn)}
        </p>
      ) : null}
    </div>
  );

  const eggTiles =
    eggs.length === 0 ? (
      <Card className="p-lg text-center text-body text-on-surface-variant">
        No eggs recorded yet.
      </Card>
    ) : (
      /* Four across, which is the common clutch on one line; a fifth or sixth egg starts
         a second row. */
      <ul aria-label="Eggs in this nest" className={cn("grid grid-cols-4", wide ? "gap-2.5" : "gap-2")}>
        {eggs.map((egg) => {
          const tone = eggTileTone(egg.status, egg.isFostered);

          return (
            <li key={egg.id}>
              <button
                type="button"
                onClick={() => setSelectedEgg(egg)}
                className={cn(
                  "flex w-full flex-col items-center justify-center gap-1.5",
                  wide ? "min-h-28 rounded-egg" : "min-h-24 rounded-card",
                  "p-sm transition-opacity hover:opacity-80",
                  tone.tile,
                )}
              >
                <EggIcon className={cn(wide ? "size-6" : "size-5.5", tone.ink)} aria-hidden />
                <span className={cn("text-on-surface", wide ? "text-data" : "text-node")} data-numeric>
                  Egg {egg.position}
                </span>
                <span className={cn(wide ? "text-micro font-semibold" : "text-node-sub font-semibold", tone.ink)}>
                  {EGG_STATUSES.find((option) => option.value === egg.status)?.label ?? egg.status}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    );

  const chickList =
    chicks.length > 0 ? (
      <section className="flex flex-col gap-md">
        <h2 className="text-section-header text-on-surface">Chicks</h2>

        <ul className="grid grid-cols-1 gap-sm xl:grid-cols-2">
          {chicks.map((chick) => (
            <li key={chick.id}>
              <button type="button" onClick={() => setSelectedChick(chick)} className="w-full text-left">
                <Card
                  shape="row"
                  className="flex min-h-touch items-center justify-between gap-md p-lg transition-colors hover:bg-surface-container-low"
                >
                  <div className="flex flex-col gap-hair">
                    <span className="text-label text-on-surface" data-numeric>
                      {chick.ringNumber ?? `From egg ${chick.eggPosition}`}
                    </span>
                    <span className="text-caption text-on-surface-variant" data-numeric>
                      Hatched {formatDay(chick.hatchedOn)}
                    </span>
                  </div>

                  <StatusPill tone={chick.birdId ? "positive" : "biological"}>{chick.status}</StatusPill>
                </Card>
              </button>
            </li>
          ))}
        </ul>
      </section>
    ) : null;

  const log = (
    <Card className={cn("flex flex-col gap-2.5", wide ? "p-lg" : "p-4")}>
      <h2 className={cn("text-on-surface", wide ? "text-pair-title" : "text-panel-title")}>Round log</h2>

      {/* Newest first, one line each: a date and what happened. */}
      <ul className="flex flex-col gap-2.5">
        {roundLog(summary).map((entry) => (
          <li
            key={entry.what}
            className={cn("flex items-center text-cell font-normal text-on-surface", wide ? "gap-3" : "gap-2.5")}
          >
            <span aria-hidden className="size-2.25 shrink-0 rounded-full bg-primary" />
            <span
              className={cn("shrink-0 font-bold text-on-surface-variant", wide ? "w-16" : "w-14")}
              data-numeric
            >
              {entry.when}
            </span>
            <span className="min-w-0 flex-1" data-numeric>
              {entry.what}
            </span>
          </li>
        ))}

        {!summary.firstEggOn ? (
          <li className="text-body text-on-surface-variant">
            Nothing recorded yet. The log fills in as the round runs.
          </li>
        ) : null}
      </ul>
    </Card>
  );

  const sheets = (
    <>
      {selectedEgg ? (
        <EggSheet egg={selectedEgg} nestId={id!} onClose={() => setSelectedEgg(null)} onError={setError} />
      ) : null}

      {selectedChick ? (
        <ChickSheet chick={selectedChick} onClose={() => setSelectedChick(null)} onError={setError} />
      ) : null}
    </>
  );

  /*
   * From `md`, and always in the split view's pane: the pair is the title, the dial
   * stands beside the eggs rather than above them, and the clutch's three dates sit in
   * a strip under the eggs. At a tablet's width a 168px dial centred in a full-width
   * card is a great deal of empty sage, and the eggs are what the screen is for.
   */
  if (wide) {
    return (
      <div className={cn("flex flex-col gap-4.5 px-7 pb-lg pt-6.5", embedded ? "" : "md:px-gutter-wide")}>
        <div className="flex items-start justify-between gap-md">
          <div className="flex min-w-0 items-start gap-3">
            {embedded ? null : (
              <Link
                to={`/breeding/pairs/${summary.pairId}`}
                aria-label="Back"
                className="flex size-touch shrink-0 items-center justify-center rounded-full bg-surface-container-lowest text-on-surface shadow-card"
              >
                <ArrowLeft className="size-5" />
              </Link>
            )}

            <div className="flex min-w-0 flex-col gap-1">
              <span className="truncate text-kicker uppercase text-on-surface-variant" data-numeric>
                {where}
              </span>
              <h1 className="truncate text-pair-wide text-on-surface" data-numeric>
                {summary.sireRingNumber ?? "Unringed"} × {summary.damRingNumber ?? "Unringed"}
              </h1>
            </div>
          </div>

          {addEggButton}
        </div>

        {error ? <Alert tone="error">{error}</Alert> : null}

        <div className="flex gap-4">
          {hero}

          <div className="flex min-w-0 flex-1 flex-col gap-3.5">
            {eggTiles}

            <dl
              className="grid grid-cols-3 rounded-card bg-surface-container px-4.5 py-3.5"
              data-numeric
            >
              {[
                { label: "First egg", value: summary.firstEggOn ? formatDay(summary.firstEggOn) : "—" },
                {
                  label: "Fertile",
                  value: summary.eggCount > 0 ? `${summary.fertileCount} of ${summary.eggCount}` : "—",
                },
                {
                  label: "Ring from",
                  value: summary.expectedRingingOn ? formatDay(summary.expectedRingingOn) : "—",
                },
              ].map((fact) => (
                <div key={fact.label} className="flex min-w-0 flex-col gap-0.5">
                  <dt className="text-fact-label uppercase text-on-surface-variant">{fact.label}</dt>
                  <dd className="truncate text-row text-on-surface">{fact.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>

        {chickList}
        {log}
        {sheets}
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <DetailHeader backTo={`/breeding/pairs/${summary.pairId}`} kicker={where} title={summary.status} />

      <div className="flex flex-col gap-4 px-5">
        {error ? <Alert tone="error">{error}</Alert> : null}

        {hero}

        <section className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-md">
            <h2 className="text-section-header text-on-surface">Eggs</h2>
            {addEggButton}
          </div>

          {eggTiles}
        </section>

        {chickList}
        {log}
      </div>

      {sheets}
    </div>
  );
}

function remaining(progress: { day: number; total: number }): string {
  const left = progress.total - progress.day;

  if (left <= 0) {
    return "Due now";
  }

  return left === 1 ? "1 day to go" : `${left} days to go`;
}

/**
 * What the round has done, newest first, as dated lines.
 *
 * Composed from the dates the summary carries rather than fetched: there is no event log
 * behind a nest, and the hatch and the first egg are the two moments it records a date
 * for. The clutch's state rides on the first-egg line, because it has no date of its own.
 */
function roundLog(summary: {
  firstEggOn: string | null;
  firstHatchOn: string | null;
  eggCount: number;
  fertileCount: number;
  hatchedCount: number;
  chickCount: number;
}): readonly { when: string; what: string }[] {
  const entries: { when: string; what: string }[] = [];

  if (summary.firstHatchOn) {
    entries.push({
      when: formatDay(summary.firstHatchOn),
      what: `First hatch — ${summary.hatchedCount} hatched, ${summary.chickCount} in the nest`,
    });
  }

  if (summary.firstEggOn) {
    const clear = summary.eggCount - summary.fertileCount;

    entries.push({
      when: formatDay(summary.firstEggOn),
      what:
        summary.fertileCount > 0
          ? `First egg — ${summary.eggCount} laid, ${summary.fertileCount} fertile${clear > 0 ? `, ${clear} clear or not candled` : ""}`
          : `First egg — ${summary.eggCount} laid, none candled yet`,
    });
  }

  return entries;
}

/** "Tuesday 6 Oct" — the hatch day the way it is said aloud. */
function longDay(iso: string | null | undefined): string {
  if (!iso) {
    return "—";
  }

  return new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

/** A stored date as "30 Aug". Stored UTC, read in the breeder's own calendar. */
function formatDay(iso: string | null | undefined): string {
  if (!iso) {
    return "—";
  }

  const [year, month, day] = iso.split("-").map(Number);

  if (!year || !month || !day) {
    return iso;
  }

  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

/** The per-egg actions: record its fate, or move it to another nest. */
function EggSheet({
  egg,
  nestId,
  onClose,
  onError,
}: {
  egg: EggDto;
  nestId: string;
  onClose: () => void;
  onError: (message: string) => void;
}) {
  const setStatus = useSetEggStatus();
  const foster = useFosterEgg();
  const nests = useNests(undefined);
  const [status, setStatus_] = useState<EggStatus>(egg.status);
  const [fosterTarget, setFosterTarget] = useState(egg.fosterNestId ?? "");

  async function run(action: () => Promise<unknown>): Promise<void> {
    try {
      await action();
      onClose();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "That did not work.");
      onClose();
    }
  }

  const candidates = (nests.data?.nests ?? []).filter((n) => n.id !== nestId);

  return (
    <BottomSheet
      open
      onClose={onClose}
      title={`Egg ${egg.position}`}
      footer={
        <Button
          block
          disabled={setStatus.isPending}
          onClick={() => void run(() => setStatus.mutateAsync({ eggId: egg.id, status }))}
        >
          {setStatus.isPending ? "Saving…" : "Save"}
        </Button>
      }
    >
      <div className="flex flex-col gap-md">
        <div className="flex flex-col gap-xs">
          <p className="text-label-caps uppercase text-on-surface-variant">Fate</p>
          <Picker
            label="Egg status"
            value={status}
            onChange={(value) => setStatus_(value as EggStatus)}
            options={EGG_STATUSES.map((option) => ({
              value: option.value,
              label: option.label,
            }))}
          />
          <p className="text-body text-on-surface-variant">
            Marking it hatched creates the chick in the same step.
          </p>
        </div>

        <div className="flex flex-col gap-xs border-t border-outline-variant pt-md">
          <p className="text-label-caps uppercase text-on-surface-variant">Foster to</p>
          <Picker
            label="Foster nest"
            emptyLabel="No other nest to foster into"
            value={fosterTarget}
            onChange={setFosterTarget}
            options={[
              { value: "", label: "Stay in this nest" },
              ...candidates.map((candidate) => ({
                value: candidate.id,
                label: `Round ${candidate.round}`,
                hint: `${candidate.sireRingNumber ?? "Unringed"} × ${candidate.damRingNumber ?? "Unringed"}`,
              })),
            ]}
          />

          {/* The rule that makes fostering safe, stated where the breeder acts on it. */}
          <p className="text-body text-on-surface-variant">
            Fostering moves who sits on the egg. The chick keeps the parents of the pair
            that laid it.
          </p>

          <Button
            variant="secondary"
            disabled={foster.isPending}
            onClick={() =>
              void run(() =>
                foster.mutateAsync({ eggId: egg.id, fosterNestId: fosterTarget || null }),
              )
            }
          >
            {foster.isPending ? "Moving…" : fosterTarget ? "Move egg" : "Bring back"}
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
}

/** The per-chick actions: ring it, or record that it fledged or died. */
function ChickSheet({
  chick,
  onClose,
  onError,
}: {
  chick: ChickDto;
  onClose: () => void;
  onError: (message: string) => void;
}) {
  const ring = useRingChick();
  const setStatus = useSetChickStatus();
  const [ringNumber, setRingNumber] = useState("");

  async function run(action: () => Promise<unknown>): Promise<void> {
    try {
      await action();
      onClose();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "That did not work.");
      onClose();
    }
  }

  return (
    <BottomSheet open onClose={onClose} title={chick.ringNumber ?? "Chick"}>
      <div className="flex flex-col gap-md">
        <p className="text-body text-on-surface-variant" data-numeric>
          Hatched {chick.hatchedOn}
          {chick.expectedRingingOn ? ` · ring around ${chick.expectedRingingOn}` : ""}
        </p>

        {chick.birdId ? (
          <Alert tone="success">
            Ringed as {chick.ringNumber}. It is in the bird register now.
          </Alert>
        ) : (
          <div className="flex flex-col gap-sm">
            <p className="text-body text-on-surface-variant">
              Leave the ring number blank to take the next unused ring from your batches.
            </p>
            <input
              aria-label="Ring number"
              placeholder="Next unused ring"
              value={ringNumber}
              data-numeric
              onChange={(event) => setRingNumber(event.target.value)}
              className="min-h-touch w-full rounded-control border border-outline-variant bg-surface-container-lowest px-md text-body text-on-surface"
            />
            <Button
              disabled={ring.isPending}
              onClick={() =>
                void run(() =>
                  ring.mutateAsync({
                    chickId: chick.id,
                    ...(ringNumber.trim() ? { ringNumber: ringNumber.trim() } : {}),
                  }),
                )
              }
            >
              {ring.isPending ? "Ringing…" : "Ring chick"}
            </Button>
          </div>
        )}

        <div className="flex gap-sm border-t border-outline-variant pt-md">
          <Button
            variant="secondary"
            disabled={setStatus.isPending}
            onClick={() =>
              void run(() => setStatus.mutateAsync({ chickId: chick.id, status: "Fledged" }))
            }
          >
            Fledged
          </Button>
          <Button
            variant="danger"
            disabled={setStatus.isPending}
            onClick={() =>
              void run(() => setStatus.mutateAsync({ chickId: chick.id, status: "Died" }))
            }
          >
            Died
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
}


/** "Tue 6 Oct" — the hatch day where the line is narrower. */
function shortWeekday(iso: string | null | undefined): string {
  if (!iso) {
    return "—";
  }

  return new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}
